/*
 * <bos-builder-modal> — theme-side shell for the rack builder.
 *
 *  - Lazy-loads builder.js / builder.css the first time the modal opens, so
 *    the product page's initial load never pays for the 3D app.
 *  - Mirrors the builder's `bos-builder:change` snapshot into localStorage
 *    ("bos-build-summary:<variant id>") so the buy box can show the saved
 *    build — total, parts, "you left off on" — on later visits without
 *    loading the builder at all.
 *  - ADD TO CART on the saved card posts those lines straight to
 *    /cart/add.js and opens the theme's cart drawer, the same way the
 *    builder itself does.
 *
 * Builder events (document): bos-builder:change | :added | :reset.
 */
(function () {
  'use strict';

  var TAB_LABELS = {
    rack: 'The Rack',
    attachments: 'Attachments',
    storage: 'Storage',
    review: 'Review',
  };

  function formatMoney(amount, currency, moneyFormat) {
    // Prefer the shop's money format ("CA${{amount}}"), fall back to Intl.
    if (moneyFormat && /\{\{\s*amount/.test(moneyFormat)) {
      var num = Number(amount || 0);
      var withCents = moneyFormat.indexOf('amount_no_decimals') === -1;
      var str = withCents
        ? num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Math.round(num).toLocaleString('en-US');
      return moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, str).replace(/<[^>]+>/g, '');
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(Number(amount || 0));
    } catch (e) {
      return (currency || '') + ' ' + Number(amount || 0).toFixed(2);
    }
  }

  var loaderPromise = null;

  class BosBuilderModal extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId || '';
      this.storageKey = 'bos-build-summary:' + this.productId;
      this.modal = this.querySelector('[data-bos-modal]');
      this.emptyState = this.querySelector('[data-bos-state="empty"]');
      this.savedState = this.querySelector('[data-bos-state="saved"]');
      this.listEl = this.querySelector('[data-bos-list]');
      this.errorEl = this.querySelector('[data-bos-error]');
      this.loadingEl = this.querySelector('[data-bos-loading]');
      this.summary = this.readSummary();

      // Portal the modal to <body>: inside the product column it would be
      // trapped in that column's stacking context, under the sticky header.
      if (this.modal && this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.querySelectorAll('[data-bos-open]').forEach((b) => b.addEventListener('click', () => this.open()));
      // The modal is portaled to <body>, so query it, not `this`.
      if (this.modal) {
        this.modal.querySelectorAll('[data-bos-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      }
      var add = this.querySelector('[data-bos-add]');
      if (add) add.addEventListener('click', () => this.addSavedToCart());
      var reset = this.querySelector('[data-bos-reset]');
      if (reset) reset.addEventListener('click', () => this.reset());
      var toggle = this.querySelector('[data-bos-toggle-list]');
      if (toggle) toggle.addEventListener('click', () => this.toggleList());

      this.onChange = (e) => this.setSummary(e.detail);
      this.onAdded = () => {
        // The builder just added to the cart and the cart drawer is opening
        // underneath us; get out of its way.
        this.close();
      };
      this.onReset = () => this.setSummary(null);
      this.onKey = (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          // Let the builder's own drawers/dialogs take Escape first.
          if (document.querySelector('#form-root [role="dialog"], #form-root [role="alertdialog"]')) return;
          this.close();
        }
      };
      document.addEventListener('bos-builder:change', this.onChange);
      document.addEventListener('bos-builder:added', this.onAdded);
      document.addEventListener('bos-builder:reset', this.onReset);
      document.addEventListener('keydown', this.onKey);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('bos-builder:change', this.onChange);
      document.removeEventListener('bos-builder:added', this.onAdded);
      document.removeEventListener('bos-builder:reset', this.onReset);
      document.removeEventListener('keydown', this.onKey);
    }

    /* ---------- saved build ---------- */

    readSummary() {
      try {
        var raw = localStorage.getItem(this.storageKey);
        if (!raw) return null;
        var j = JSON.parse(raw);
        return j && Array.isArray(j.lines) && j.lines.length ? j : null;
      } catch (e) {
        return null;
      }
    }

    setSummary(snapshot) {
      if (!snapshot || !snapshot.touched || !Array.isArray(snapshot.lines) || !snapshot.lines.length) {
        this.summary = null;
        try { localStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
      } else {
        this.summary = {
          total: snapshot.total,
          currency: snapshot.currency,
          itemCount: snapshot.itemCount,
          canAdd: snapshot.canAdd !== false,
          tab: snapshot.tab,
          tabLabel: snapshot.tabLabel,
          lines: snapshot.lines,
          savedAt: Date.now(),
        };
        try { localStorage.setItem(this.storageKey, JSON.stringify(this.summary)); } catch (e) { /* ignore */ }
      }
      this.render();
    }

    render() {
      var s = this.summary;
      if (this.emptyState) this.emptyState.hidden = !!s;
      if (this.savedState) this.savedState.hidden = !s;
      if (!s) return;
      var n = s.itemCount || s.lines.reduce((a, l) => a + (l.quantity || 0), 0);
      var parts = this.querySelector('[data-bos-parts]');
      if (parts) parts.textContent = n + (n === 1 ? ' part' : ' parts');
      var total = this.querySelector('[data-bos-total]');
      if (total) total.textContent = formatMoney(s.total, s.currency || this.dataset.currency, this.dataset.moneyFormat);
      var tab = this.querySelector('[data-bos-tab]');
      if (tab) tab.textContent = s.tabLabel || TAB_LABELS[s.tab] || 'The Rack';
      var add = this.querySelector('[data-bos-add]');
      if (add) add.disabled = !s.canAdd;
      if (this.listEl && !this.listEl.hidden) this.renderList();
    }

    renderList() {
      var s = this.summary;
      if (!this.listEl || !s) return;
      this.listEl.innerHTML = '';
      s.lines.forEach((l) => {
        var row = document.createElement('div');
        row.className = 'bos-builder__line';
        var name = document.createElement('span');
        name.className = 'bos-builder__line-name';
        name.textContent = l.title || l.sku || ('Variant ' + l.variantId);
        var detail = document.createElement('span');
        detail.className = 'bos-builder__line-detail';
        detail.textContent = [l.group, l.quantity > 1 ? '×' + l.quantity : ''].filter(Boolean).join(' · ');
        name.appendChild(detail);
        row.appendChild(name);
        if (l.lineTotal != null) {
          var price = document.createElement('span');
          price.className = 'bos-builder__line-price';
          price.textContent = formatMoney(l.lineTotal, s.currency || this.dataset.currency, this.dataset.moneyFormat);
          row.appendChild(price);
        }
        this.listEl.appendChild(row);
      });
    }

    toggleList() {
      if (!this.listEl) return;
      var open = this.listEl.hidden;
      this.listEl.hidden = !open;
      var btn = this.querySelector('[data-bos-toggle-list]');
      if (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.innerHTML = open ? 'Hide the parts list &#9652;' : 'See the parts list &#9662;';
      }
      if (open) this.renderList();
    }

    reset() {
      if (window.BOSBuilder && typeof window.BOSBuilder.reset === 'function') {
        window.BOSBuilder.reset(); // emits bos-builder:reset → setSummary(null)
      }
      // Also clear the builder's own saved build so a later open starts fresh.
      try {
        Object.keys(localStorage)
          .filter((k) => k.indexOf('bos-build:') === 0 && k.indexOf(':' + this.productId) !== -1)
          .forEach((k) => localStorage.removeItem(k));
      } catch (e) { /* ignore */ }
      this.setSummary(null);
    }

    /* ---------- add to cart from the buy box ---------- */

    async addSavedToCart() {
      var s = this.summary;
      var btn = this.querySelector('[data-bos-add]');
      if (!s || !btn || btn.disabled) return;
      this.showError('');
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        var items = s.lines
          .filter((l) => l.variantId > 0 && l.quantity > 0)
          .map((l) => ({ id: String(l.variantId), quantity: l.quantity, properties: l.properties || {} }));
        var cartDrawer = document.querySelector('cart-drawer');
        var sections = ['cart-icon-bubble'];
        var parent = cartDrawer && cartDrawer.closest('.shopify-section');
        if (parent && parent.id) sections.push(parent.id.replace('shopify-section-', ''));

        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items, sections: sections.join(','), sections_url: window.location.pathname }),
        });
        var data = await res.json();
        if (!res.ok) {
          this.showError(data.description || data.message || 'Something went wrong adding your build to the cart.');
          return;
        }
        if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
          await cartDrawer.renderContents({ sections: data.sections }, true);
        } else {
          var cartItems = document.querySelector('cart-items');
          if (cartItems && typeof cartItems.refresh === 'function') await cartItems.refresh();
          else if (data.sections && data.sections['cart-icon-bubble']) {
            var bubble = document.getElementById('cart-icon-bubble');
            if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
          }
        }
      } catch (e) {
        this.showError('Something went wrong adding your build to the cart. Please try again.');
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }

    /* ---------- modal ---------- */

    isOpen() {
      return !!this.modal && !this.modal.hidden;
    }

    loadBuilder() {
      if (loaderPromise) return loaderPromise;
      var css = this.dataset.builderCss;
      var js = this.dataset.builderJs;
      loaderPromise = new Promise((resolve, reject) => {
        var pending = 2;
        var done = () => { if (--pending === 0) resolve(); };
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = css;
        link.onload = done;
        link.onerror = () => reject(new Error('builder.css failed to load'));
        document.head.appendChild(link);
        var script = document.createElement('script');
        script.type = 'module';
        script.src = js;
        script.onload = done;
        script.onerror = () => reject(new Error('builder.js failed to load'));
        document.head.appendChild(script);
      });
      return loaderPromise;
    }

    async open() {
      if (!this.modal) return;
      this.lastFocus = document.activeElement;
      this.modal.hidden = false;
      document.body.classList.add('bos-modal-open');
      var close = this.modal.querySelector('.bos-modal__close');
      if (close) close.focus();
      try {
        await this.loadBuilder();
        if (this.loadingEl) this.loadingEl.remove();
        this.loadingEl = null;
      } catch (e) {
        if (this.loadingEl) this.loadingEl.textContent = 'The builder could not be loaded. Please refresh and try again.';
      }
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.classList.remove('bos-modal-open');
      // Being inside the builder is what creates a build; make sure the buy
      // box reflects it even if no change event fired yet.
      if (window.BOSBuilder && typeof window.BOSBuilder.snapshot === 'function') {
        this.setSummary(window.BOSBuilder.snapshot());
      }
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }
  }

  if (!customElements.get('bos-builder-modal')) {
    customElements.define('bos-builder-modal', BosBuilderModal);
  }
})();
